
import React from 'react'
import { Trophy, Target, Users, DollarSign, MessageCircle, TrendingUp, Shield, Wallet } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useChallenges } from '@/hooks/useChallenges'

const ChallengesCard = () => {
  const { getDashboardChallenges, loading } = useChallenges()
  const dashboardChallenges = getDashboardChallenges()

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'users': return Users
      case 'dollar-sign': return DollarSign
      case 'trending-up': return TrendingUp
      case 'target':
      default: return Target
    }
  }

  const getColorClass = (color: string, type: 'bg' | 'text' | 'border') => {
    const colorMap = {
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
      green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200' },
      red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200' }
    }
    return colorMap[color as keyof typeof colorMap]?.[type] || colorMap.purple[type]
  }

  const ChallengeItem = ({ module, challengeColor }: { module: any, challengeColor: string }) => (
    <div className="p-4 rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all cursor-pointer">
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg ${getColorClass(challengeColor, 'bg')} flex items-center justify-center flex-shrink-0`}>
          <Target className={`w-4 h-4 ${getColorClass(challengeColor, 'text')}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 break-words leading-relaxed mb-1">{module.title}</h4>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">Module</span>
            <div className={`w-4 h-4 rounded-full border-2 ${getColorClass(challengeColor, 'border')} flex items-center justify-center`}>
              <div className={`w-1.5 h-1.5 rounded-full ${getColorClass(challengeColor, 'bg')}`}></div>
            </div>
          </div>
          <Progress value={0} className="h-1.5" />
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-4 flex-shrink-0">
          <CardTitle className="text-xl font-semibold text-gray-900">Challenges</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">Loading challenges...</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-4 flex-shrink-0">
        <CardTitle className="text-xl font-semibold text-gray-900">Challenges</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
          {dashboardChallenges.map((challenge, index) => {
            const IconComponent = getIconComponent(challenge.icon)
            const completedModules = challenge.user_progress?.completed_modules || 0
            const totalModules = challenge.challenge_modules?.length || 3

            return (
              <div key={challenge.id} className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <IconComponent className={`w-5 h-5 ${getColorClass(challenge.color, 'text')}`} />
                    <div>
                      <h3 className="font-semibold text-gray-900">{challenge.category}</h3>
                      <p className="text-sm text-gray-500">{completedModules}/{totalModules} completed</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm font-medium text-orange-600">
                    <Trophy className="w-4 h-4" />
                    {challenge.points}
                  </div>
                </div>
                <ScrollArea className="flex-1">
                  <div className="space-y-3 pr-4">
                    {challenge.challenge_modules?.slice(0, 3).map((challengeModule) => (
                      <ChallengeItem 
                        key={challengeModule.id} 
                        module={challengeModule.learn_modules}
                        challengeColor={challenge.color}
                      />
                    ))}
                  </div>
                </ScrollArea>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={`w-full mt-4 flex-shrink-0 ${getColorClass(challenge.color, 'text')} ${getColorClass(challenge.color, 'border')}`}
                  style={{ 
                    backgroundColor: `${getColorClass(challenge.color, 'bg').replace('bg-', '')}10`
                  }}
                >
                  View Challenge Details
                </Button>
              </div>
            )
          })}
          
          {dashboardChallenges.length === 0 && (
            <div className="col-span-2 flex flex-col items-center justify-center py-8 text-center">
              <Trophy className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-500">No challenges available</p>
              <p className="text-sm text-gray-400">Check back later for new challenges</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default ChallengesCard
